/**
 * Figma prototype as a stimulus (FaceWise Pro, Z-98).
 *
 * The researcher side only: connecting a Figma account, reading a prototype's
 * screens for the wizard, and the per-screen results. The respondent never
 * talks to these endpoints — the prototype plays from Figma's own embed.
 */

import { baseService } from './baseService'
import type { EmotionKey } from './emotionAnalysisService'
import type { AnswerFilters } from './emotionAnalysisService'

export interface FigmaFrame {
  node_id: string
  name: string
  width: number
  height: number
  image_url: string
}

export interface FigmaConnectionState {
  connected: boolean
  /** Figma e-mail or display name of the connected account. */
  handle: string
}

export interface FigmaInspection {
  file_key: string
  start_node_id: string
  frames: FigmaFrame[]
  /** Whether the file opens for someone not logged in to Figma — every
   *  respondent. `null` when Figma could not be asked. */
  is_public: boolean | null
}

export interface PrototypeClick {
  x: number
  y: number
  handled: boolean
}

export interface PrototypeScreen {
  node_id: string
  name: string
  width: number
  height: number
  image_url: string
  is_start: boolean
  is_goal: boolean
  visits: number
  respondents: number
  median_time_ms: number
  clicks: PrototypeClick[]
  misclicks: number
  /** Mean emotion mix while on this screen; null when no face was analysed there. */
  emotions: Record<EmotionKey, number> | null
  emotion_respondents: number
  /** Gaze points on the screen (0..1); null when the study has no eye tracking. */
  gaze: Array<{ x: number; y: number }> | null
}

export type PrototypeOutcome = 'success' | 'gave_up' | 'timeout' | 'finished'

export interface PrototypeResults {
  stimulus_id: number | null
  task: string
  goal_node_id: string
  time_limit_ms: number | null
  eye_tracking: boolean
  summary: {
    sessions: number
    success: number
    gave_up: number
    timeout: number
    finished: number
    has_goal: boolean
    success_rate: number | null
    median_success_ms: number | null
    median_duration_ms: number | null
    misclick_rate: number | null
  } | null
  screens: PrototypeScreen[]
  sessions: Array<{
    respondent_id: number
    outcome: PrototypeOutcome
    duration_ms: number
    clicks: number
    misclicks: number
    path: string[]
  }>
  /** One respondent's session on the stimulus clock — drives the replay. */
  individual: {
    respondent_id: number
    outcome: PrototypeOutcome
    duration_ms: number
    screens: Array<{ node_id: string; start_ms: number; end_ms: number }>
    clicks: Array<{ t_ms: number; node_id: string; x: number | null; y: number | null; handled: boolean }>
    /** Gaze on the screen picture (0..1), stimulus clock. Null without eye tracking. */
    gaze: Array<{ t_ms: number; node_id: string; x: number; y: number }> | null
  } | null
}

class PrototypeService {
  async connection(): Promise<FigmaConnectionState> {
    return (await baseService.get<FigmaConnectionState>('/figma/connection/')).data
  }

  async disconnect(): Promise<void> {
    await baseService.delete('/figma/connection/')
  }

  /** The Figma consent page, opened by the wizard in a popup. */
  async oauthUrl(): Promise<string> {
    return (await baseService.get<{ url: string }>('/figma/oauth/url/')).data.url
  }

  async inspect(url: string): Promise<FigmaInspection> {
    return (await baseService.post<FigmaInspection>('/figma/inspect/', { url })).data
  }

  async results(
    projectId: number,
    query: { stimulusId: number; respondentId?: number | null; filters?: AnswerFilters },
  ): Promise<PrototypeResults> {
    const params = new URLSearchParams({ stimulus_id: String(query.stimulusId) })
    if (query.respondentId != null) params.set('respondent_id', String(query.respondentId))
    for (const [qid, values] of Object.entries(query.filters ?? {})) {
      for (const v of values) params.append('answer', `${qid}:${v}`)
    }
    return (await baseService.get<PrototypeResults>(`/projects/${projectId}/prototype-results/?${params}`)).data
  }
}

export const prototypeService = new PrototypeService()
